import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import LoadingSpinner from '@components/common/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import {
  testConfigService,
  type TestConfig,
  type WcagCriterion,
} from '@services/testConfigService';
import TestConfigForm from './TestConfigForm';

export interface OrgTestConfigSectionProps {
  orgId: string;
}

/**
 * REV-461/462: organization-level default test configuration. These defaults
 * are inherited by every tracked place in the org (and can be overridden per
 * context and per scan).
 */
export default function OrgTestConfigSection({ orgId }: OrgTestConfigSectionProps) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<TestConfig | null>(null);
  // REV-486: what an unset field resolves to, so the form can name it
  // instead of rendering blank. The API already returns these.
  const [defaults, setDefaults] = useState<TestConfig | undefined>(undefined);
  // REV-486: fetched once here and passed down, so the form stays
  // presentational and the catalog is not re-fetched on every render.
  const [criteria, setCriteria] = useState<WcagCriterion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { config: cfg, defaults: defs } = await testConfigService.getOrgConfig(orgId);
      setConfig(cfg);
      setDefaults(defs);
      testConfigService
        .getWcagCriteria(cfg.wcag?.version || defs?.wcag?.version)
        .then(setCriteria)
        // The type-ahead simply offers nothing rather than blocking the form.
        .catch(() => setCriteria([]));
    } catch {
      setError(t('testConfig.errors.loadFailed', 'Could not load the test configuration.'));
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async (next: TestConfig) => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await testConfigService.putOrgConfig(orgId, next);
      setConfig(updated);
      setSaved(true);
    } catch {
      setError(t('testConfig.errors.saveFailed', 'Could not save the test configuration.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        {t('testConfig.orgHeading', 'Default test configuration')}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" role="status" sx={{ mb: 2 }} onClose={() => setSaved(false)}>
          {t('testConfig.saved', 'Test configuration saved.')}
        </Alert>
      )}
      {loading || !config ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <LoadingSpinner size={24} />
        </Box>
      ) : (
        <TestConfigForm
          value={config}
          defaults={defaults}
          criteria={criteria}
          onSave={handleSave}
          saving={saving}
          level="organization"
        />
      )}
    </Box>
  );
}
